import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';

export default function HomePage() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('company', formData.company);
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d69nq2ma728k8ctu2rog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ company: '', name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-vintage-cream">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-vintage-cream/95 backdrop-blur-sm shadow-sm' : 'bg-vintage-cream/95 backdrop-blur-sm'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-5 flex items-center justify-between">
          <div className="font-display text-xl sm:text-2xl tracking-[0.2em] text-vintage-dark">
            <span className="font-semibold">SECOND</span>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap">{t('nav.about')}</button>
            <button onClick={() => scrollToSection('business')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap">{t('nav.business')}</button>
            <button onClick={() => scrollToSection('recruitment')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap">{t('nav.recruitment')}</button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-serif tracking-[0.15em] border border-vintage-dark text-vintage-dark px-6 py-2 rounded-sm hover:bg-vintage-dark hover:text-vintage-cream transition-all cursor-pointer whitespace-nowrap">{t('nav.contact')}</button>
            <LanguageSwitcher />
          </div>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 lg:hidden">
            <LanguageSwitcher />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="w-10 h-10 flex items-center justify-center text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer"
              aria-label="メニュー"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-vintage-cream border-t border-vintage-sand">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3">
              <button onClick={() => scrollToSection('about')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap text-left py-2">{t('nav.about')}</button>
              <button onClick={() => scrollToSection('business')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap text-left py-2">{t('nav.business')}</button>
              <button onClick={() => scrollToSection('recruitment')} className="text-sm font-serif tracking-[0.15em] text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap text-left py-2">{t('nav.recruitment')}</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-serif tracking-[0.15em] border border-vintage-dark text-vintage-dark px-6 py-2 rounded-sm hover:bg-vintage-dark hover:text-vintage-cream transition-all cursor-pointer whitespace-nowrap text-center mt-2">{t('nav.contact')}</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-vintage-cream via-[#F0EBE3] to-[#E8E0D4]">
          <div className="absolute inset-0 bg-white/20"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-8 pt-32 pb-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14 items-center">
            <div className="lg:col-span-3">
              {/* Decorative line */}
              <div className="flex items-center gap-4 mb-10">
                <div className="w-16 h-px bg-vintage-gold"></div>
                <span className="text-vintage-terracotta font-serif text-sm tracking-[0.3em] italic">Since 2006</span>
              </div>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl text-vintage-dark leading-tight mb-8 tracking-wide">
                {t('hero.title1')}<br />
                <span className="italic font-normal text-vintage-terracotta">{t('hero.title2')}</span>
              </h1>

              <div className="w-12 h-px bg-vintage-gold mb-8"></div>

              <p className="text-lg sm:text-xl lg:text-2xl text-vintage-brown font-serif font-normal leading-relaxed mb-12 tracking-wide">
                {t('hero.subtitle1')}<br />{t('hero.subtitle2')}
              </p>

              <button
                onClick={() => scrollToSection('business')}
                className="group inline-flex items-center gap-4 bg-vintage-dark text-vintage-cream px-10 py-4 rounded-sm font-serif text-base tracking-[0.15em] hover:bg-vintage-brown transition-all duration-500 cursor-pointer whitespace-nowrap"
              >
                {t('hero.cta')}
                <i className="ri-arrow-right-line text-lg group-hover:translate-x-1 transition-transform"></i>
              </button>
            </div>

            <div className="lg:col-span-2 hidden lg:block">
              <div className="w-full h-[520px] rounded-sm overflow-hidden shadow-xl border border-vintage-sand/60">
                <img
                  src="https://public.readdy.ai/ai/img_res/0ca436bd77ec74c4a856d680b2e77184.jpg"
                  alt="日本製高品質繊維素材"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Decorative bottom border */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-vintage-gold/40 to-transparent"></div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 sm:py-28 bg-vintage-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 sm:mb-20">
            <span className="font-serif italic text-vintage-terracotta text-sm tracking-[0.3em]">{t('about.label')}</span>
            <div className="flex items-center justify-center gap-6 mt-4 mb-6">
              <div className="w-20 h-px bg-vintage-warm"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-vintage-terracotta"></div>
              <div className="w-20 h-px bg-vintage-warm"></div>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-vintage-dark tracking-wide">{t('about.title')}</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-20">
            {/* Left: Description + Image */}
            <div className="space-y-8">
              <p className="text-base leading-[2] text-vintage-brown font-sans">
                {t('about.description1')}
              </p>
              <p className="text-base leading-[2] text-vintage-brown font-sans">
                {t('about.description2')}
              </p>
              <p className="text-base leading-[2] text-vintage-brown font-sans">
                {t('about.description3')}
              </p>

              <div className="mt-10 rounded-sm overflow-hidden shadow-md border border-vintage-sand">
                <img
                  src="https://public.readdy.ai/ai/img_res/edited_ab2845a87a12fdca98069dafac031d72_341f36e6.jpg"
                  alt="株式会社セ・カンド 本社ビル外観"
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>

            {/* Right: Company Info Card */}
            <div className="bg-white rounded-sm shadow-md border border-vintage-sand/60 p-8 sm:p-12">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-8 h-px bg-vintage-terracotta"></div>
                <span className="font-serif italic text-vintage-terracotta text-sm tracking-wider">Company Profile</span>
              </div>

              <div className="space-y-0">
                {[
                  { label: t('about.companyName'), value: t('about.companyNameValue') },
                  { label: t('about.englishName'), value: t('about.englishNameValue') },
                  { label: t('about.established'), value: t('about.establishedValue') },
                  { label: t('about.representative'), value: t('about.representativeValue') },
                  { label: t('about.capital'), value: t('about.capitalValue') },
                  { label: t('about.employees'), value: t('about.employeesValue') },
                  { label: t('about.address'), value: <>{t('about.addressValue1')}<br />{t('about.addressValue2')}</> },
                  { label: t('about.phone'), value: t('about.phoneValue') },
                ].map((item, index) => (
                  <div key={index} className="flex flex-col sm:flex-row py-5 border-b border-dashed border-vintage-sand last:border-b-0">
                    <span className="text-xs tracking-wider text-vintage-copper sm:w-28 flex-shrink-0 pt-0.5 font-sans mb-1 sm:mb-0">{item.label}</span>
                    <span className="text-sm text-vintage-dark font-sans">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Section */}
      <section id="business" className="py-20 sm:py-28 bg-vintage-linen">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          {/* Section Header */}
          <div className="text-center mb-16 sm:mb-20">
            <span className="font-serif italic text-vintage-terracotta text-sm tracking-[0.3em]">{t('business.label')}</span>
            <div className="flex items-center justify-center gap-6 mt-4 mb-6">
              <div className="w-20 h-px bg-vintage-warm"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-vintage-terracotta"></div>
              <div className="w-20 h-px bg-vintage-warm"></div>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-vintage-dark tracking-wide">{t('business.title')}</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-white rounded-sm border border-vintage-sand/60 p-10 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group">
              <div className="w-16 h-16 border border-vintage-terracotta/40 rounded-full flex items-center justify-center mb-8 group-hover:bg-vintage-terracotta/10 transition-colors">
                <i className="ri-palette-line text-2xl text-vintage-terracotta"></i>
              </div>
              <h3 className="font-display text-xl text-vintage-dark mb-4 tracking-wide">{t('business.planning.title')}</h3>
              <div className="w-8 h-px bg-vintage-gold mb-4"></div>
              <p className="text-sm leading-[1.9] text-vintage-brown font-sans">
                {t('business.planning.description')}
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-white rounded-sm border border-vintage-sand/60 p-10 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group">
              <div className="w-16 h-16 border border-vintage-terracotta/40 rounded-full flex items-center justify-center mb-8 group-hover:bg-vintage-terracotta/10 transition-colors">
                <i className="ri-ship-line text-2xl text-vintage-terracotta"></i>
              </div>
              <h3 className="font-display text-xl text-vintage-dark mb-4 tracking-wide">{t('business.export.title')}</h3>
              <div className="w-8 h-px bg-vintage-gold mb-4"></div>
              <p className="text-sm leading-[1.9] text-vintage-brown font-sans">
                {t('business.export.description')}
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-white rounded-sm border border-vintage-sand/60 p-10 hover:shadow-lg hover:-translate-y-1 transition-all duration-500 group">
              <div className="w-16 h-16 border border-vintage-terracotta/40 rounded-full flex items-center justify-center mb-8 group-hover:bg-vintage-terracotta/10 transition-colors">
                <i className="ri-checkbox-circle-line text-2xl text-vintage-terracotta"></i>
              </div>
              <h3 className="font-display text-xl text-vintage-dark mb-4 tracking-wide">{t('business.quality.title')}</h3>
              <div className="w-8 h-px bg-vintage-gold mb-4"></div>
              <p className="text-sm leading-[1.9] text-vintage-brown font-sans">
                {t('business.quality.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Decorative Divider with Image */}
      <section className="relative h-72 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Close%20up%20of%20luxurious%20European%20vintage%20fabric%20rolls%20and%20textile%20materials%20in%20warm%20earth%20tones%2C%20elegant%20cream%20and%20terracotta%20colored%20silk%20and%20linen%20fabrics%20stacked%20beautifully%2C%20soft%20golden%20ambient%20lighting%2C%20artisan%20textile%20workshop%20atmosphere%2C%20classical%20European%20craftsmanship%2C%20muted%20warm%20color%20palette%20with%20sepia%20undertones&width=1920&height=500&seq=divider-textile&orientation=landscape"
          alt="繊維素材"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-vintage-dark/40"></div>
        <div className="absolute inset-0 flex items-center justify-center px-4">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="w-12 sm:w-24 h-px bg-vintage-gold/60"></div>
            <div className="w-2 h-2 rounded-full bg-vintage-gold"></div>
            <span className="font-serif italic text-vintage-cream text-sm sm:text-lg tracking-[0.3em]">Quality &amp; Trust</span>
            <div className="w-2 h-2 rounded-full bg-vintage-gold"></div>
            <div className="w-12 sm:w-24 h-px bg-vintage-gold/60"></div>
          </div>
        </div>
      </section>

      {/* Recruitment Section */}
      <section id="recruitment" className="py-20 sm:py-28 bg-vintage-cream">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 sm:gap-16">
            {/* Left */}
            <div className="lg:col-span-2">
              <span className="font-serif italic text-vintage-terracotta text-sm tracking-[0.3em]">{t('recruitment.label')}</span>
              <div className="flex items-center gap-4 mt-4 mb-6">
                <div className="w-12 h-px bg-vintage-warm"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-vintage-terracotta"></div>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-vintage-dark mb-6 tracking-wide">{t('recruitment.title')}</h2>
              <p className="text-lg lg:text-xl text-vintage-brown font-serif italic mb-12">{t('recruitment.subtitle')}</p>
              <div className="grid grid-cols-1 gap-3">
                <div className="rounded-sm overflow-hidden shadow-md border border-vintage-sand">
                  <img
                    src="https://public.readdy.ai/ai/img_res/edited_cea2e26396fc5354817f827e75df659a_2c843e6b.jpg"
                    alt="明るく快適なオフィス環境"
                    className="w-full h-56 object-cover object-top"
                  />
                </div>
                <div className="rounded-sm overflow-hidden shadow-md border border-vintage-sand">
                  <img
                    src="https://storage.readdy-site.link/project_files/5c854246-9835-4df4-99cf-c0b68a5e14e5/c2efbfdf-98f4-4aac-94eb-dc99d4c8f7d7_IMG_20260225_170420.jpg?v=0b37f7adc89a3cdc3546dd59230b458e"
                    alt="職場の様子"
                    className="w-full h-56 object-cover object-top"
                  />
                </div>
              </div>
            </div>

            {/* Right */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-sm shadow-md border border-vintage-sand/60 p-8 sm:p-14">
                <h3 className="font-display text-xl sm:text-2xl text-vintage-dark mb-10 tracking-wide">{t('recruitment.position')}</h3>

                <div className="space-y-7">
                  {[
                    { icon: 'ri-briefcase-line', label: t('recruitment.jobTitle'), value: t('recruitment.jobTitleValue') },
                    { icon: 'ri-user-line', label: t('recruitment.employmentType'), value: t('recruitment.employmentTypeValue') },
                    { icon: 'ri-time-line', label: t('recruitment.workingHours'), value: t('recruitment.workingHoursValue') },
                    { icon: 'ri-map-pin-line', label: t('recruitment.location'), value: t('recruitment.locationValue') },
                    { icon: 'ri-mail-line', label: t('recruitment.howToApply'), value: t('recruitment.howToApplyValue') },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 pb-7 border-b border-dashed border-vintage-sand last:border-b-0 last:pb-0">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className={`${item.icon} text-xl text-vintage-terracotta`}></i>
                      </div>
                      <div>
                        <div className="text-xs text-vintage-copper mb-1 tracking-wider font-sans">{item.label}</div>
                        <div className="text-sm text-vintage-dark font-sans">{item.value}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10 bg-vintage-linen rounded-sm p-6 border border-vintage-sand/40">
                  <h4 className="font-display text-base text-vintage-dark mb-4 tracking-wide">
                    <a href="#recruitment" className="hover:text-vintage-terracotta transition-colors">{t('recruitment.atmosphere')}</a>
                  </h4>
                  <ul className="space-y-3">
                    {[t('recruitment.atmosphere1'), t('recruitment.atmosphere2'), t('recruitment.atmosphere3')].map((text, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-vintage-brown font-sans">
                        <i className="ri-check-line text-base text-vintage-terracotta flex-shrink-0 mt-0.5"></i>
                        <span>{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 sm:py-28 bg-vintage-linen">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="font-serif italic text-vintage-terracotta text-sm tracking-[0.3em]">{t('contact.label')}</span>
            <div className="flex items-center justify-center gap-6 mt-4 mb-6">
              <div className="w-20 h-px bg-vintage-warm"></div>
              <div className="w-1.5 h-1.5 rounded-full bg-vintage-terracotta"></div>
              <div className="w-20 h-px bg-vintage-warm"></div>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-vintage-dark tracking-wide">{t('contact.title')}</h2>
          </div>

          <div className="bg-white rounded-sm shadow-md border border-vintage-sand/60 p-8 sm:p-14">
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
              <div className="mb-8">
                <label htmlFor="company" className="block text-xs text-vintage-copper mb-2 tracking-wider font-sans">{t('contact.company')}</label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full h-12 px-4 bg-vintage-cream/50 border border-vintage-sand rounded-sm focus:outline-none focus:border-vintage-terracotta transition-colors text-sm font-sans text-vintage-dark"
                />
              </div>

              <div className="mb-8">
                <label htmlFor="name" className="block text-xs text-vintage-copper mb-2 tracking-wider font-sans">{t('contact.name')} <span className="text-vintage-terracotta">*</span></label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full h-12 px-4 bg-vintage-cream/50 border border-vintage-sand rounded-sm focus:outline-none focus:border-vintage-terracotta transition-colors text-sm font-sans text-vintage-dark"
                  required
                />
              </div>

              <div className="mb-8">
                <label htmlFor="email" className="block text-xs text-vintage-copper mb-2 tracking-wider font-sans">{t('contact.email')} <span className="text-vintage-terracotta">*</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full h-12 px-4 bg-vintage-cream/50 border border-vintage-sand rounded-sm focus:outline-none focus:border-vintage-terracotta transition-colors text-sm font-sans text-vintage-dark"
                  required
                />
              </div>

              <div className="mb-10">
                <label htmlFor="message" className="block text-xs text-vintage-copper mb-2 tracking-wider font-sans">{t('contact.message')} <span className="text-vintage-terracotta">*</span></label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({...formData, message: e.target.value});
                    }
                  }}
                  rows={6}
                  className="w-full px-4 py-3 bg-vintage-cream/50 border border-vintage-sand rounded-sm focus:outline-none focus:border-vintage-terracotta transition-colors resize-none text-sm font-sans text-vintage-dark"
                  required
                  maxLength={500}
                ></textarea>
                <div className="text-xs text-vintage-copper mt-1 text-right">{formData.message.length}/500{t('contact.characters')}</div>
              </div>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-sm text-green-700 text-sm font-sans">
                  {t('contact.success')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm text-red-700 text-sm font-sans">
                  {t('contact.error')}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-14 bg-vintage-dark text-vintage-cream font-serif text-base tracking-[0.15em] rounded-sm hover:bg-vintage-brown transition-all duration-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? t('contact.submitting') : t('contact.submit')}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-vintage-sand py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12 mb-12">
            <div className="md:col-span-1">
              <div className="font-display text-2xl tracking-[0.2em] text-vintage-dark mb-4">SECOND</div>
              <p className="text-sm text-vintage-brown leading-relaxed whitespace-pre-line font-sans">
                {t('footer.description')}
              </p>
            </div>

            <div>
              <h4 className="text-xs text-vintage-copper mb-4 tracking-[0.2em] font-sans">
                <a href="#about" className="hover:text-vintage-terracotta transition-colors">{t('footer.companyInfo')}</a>
              </h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('about')} className="text-sm text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap font-sans">{t('nav.about')}</button></li>
                <li><button onClick={() => scrollToSection('business')} className="text-sm text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap font-sans">{t('nav.business')}</button></li>
                <li><button onClick={() => scrollToSection('recruitment')} className="text-sm text-vintage-dark hover:text-vintage-terracotta transition-colors cursor-pointer whitespace-nowrap font-sans">{t('nav.recruitment')}</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs text-vintage-copper mb-4 tracking-[0.2em] font-sans">
                <a href="#contact" className="hover:text-vintage-terracotta transition-colors">{t('footer.contact')}</a>
              </h4>
              <ul className="space-y-3">
                <li className="text-sm text-vintage-dark font-sans">TEL: {t('about.phoneValue')}</li>
                <li className="text-sm text-vintage-dark font-sans">{t('about.addressValue1')}<br />{t('about.addressValue2')}</li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs text-vintage-copper mb-4 tracking-[0.2em] font-sans">
                <a href="#contact" className="hover:text-vintage-terracotta transition-colors">{t('footer.businessHours')}</a>
              </h4>
              <ul className="space-y-3">
                <li className="text-sm text-vintage-dark font-sans">{t('footer.weekday')}</li>
                <li className="text-xs text-vintage-copper font-sans">{t('footer.holiday')}</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-dashed border-vintage-warm pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs text-vintage-copper font-sans">{t('footer.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
